import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Workplace, Visit } from '../../types';
import { visitApi, workplaceDocumentApi, archiveApi, workplaceApi } from '../../services/api';
import PlanVisitModal from './PlanVisitModal';
import UploadDocumentModal from './UploadDocumentModal';
import ViewDocumentModal, { StoredDocumentFile } from './ViewDocumentModal';
import EditWorkplaceModal from './EditWorkplaceModal';
import { getProgressTheme } from '../../utils/progressTheme';
import { formatDate, canMarkVisitAsCompleted } from '../../utils/dateUtils';
import Swal from 'sweetalert2';
import './WorkplaceDetailModal.css';

interface WorkplaceDetailModalProps {
  workplace: Workplace | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (workplace: Workplace) => void;
  onDelete: (id: number) => void;
  onUpdate?: () => void;
  onWorkplaceChange?: (id: number, partialData: Partial<Workplace>) => void;
}

// 6331 Sayılı İSG Mevzuatında ve Sistemde Kayıtlı Resmi 18 Zorunlu İSG Evrakı (Statik Tanım - Render Dışı)
const OFFICIAL_ISG_DOCUMENTS = [
  {
    id: 1,
    code: 'riskAnalysis',
    title: '🛡️ Risk Değerlendirme Raporu',
    validity: (danger: string) => danger === 'veryDangerous' ? '2 Yılda Bir' : danger === 'dangerous' ? '4 Yılda Bir' : '6 Yılda Bir',
    legalBasis: '6331 Sayılı Kanun Madde 10',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 2,
    code: 'emergencyPlan',
    title: '🚨 Acil Durum Eylem Planı',
    validity: (danger: string) => danger === 'veryDangerous' ? '2 Yılda Bir' : danger === 'dangerous' ? '4 Yılda Bir' : '6 Yılda Bir',
    legalBasis: '6331 Sayılı Kanun Madde 11',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 3,
    code: 'trainingPlan',
    title: '📅 Yıllık Eğitim Planı',
    validity: () => 'Yılda 1 (01 Ocak Bitiş)',
    legalBasis: 'Çalışanların İSG Eğitimleri Yönetmeliği',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 4,
    code: 'workPlan',
    title: '📋 Yıllık Çalışma Planı',
    validity: () => 'Yılda 1 (01 Ocak Bitiş)',
    legalBasis: 'İSG Hizmetleri Yönetmeliği Madde 8',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 5,
    code: 'instructions',
    title: '📄 İSG Talimatları & Prosedürler',
    validity: () => 'Sürekli / Güncel',
    legalBasis: 'İş Ekipmanları & Genel İSG Talimatları',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 6,
    code: 'ek2',
    title: '🩺 Ek-2 İşe Giriş & Periyodik Muayene',
    validity: (danger: string) => danger === 'veryDangerous' ? 'Yılda 1' : danger === 'dangerous' ? '3 Yılda 1' : '5 Yılda 1',
    legalBasis: 'İşyeri Hekimi Görevleri Yönetmeliği',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Takip Bekliyor'
  },
  {
    id: 7,
    code: 'trainings',
    title: '🎓 Eğitimler ve Oryantasyon Belgeleri',
    validity: (danger: string) => danger === 'veryDangerous' ? 'Yılda 1 (16 Saat)' : danger === 'dangerous' ? '2 Yılda 1 (12 Saat)' : '3 Yılda 1 (8 Saat)',
    legalBasis: 'Çalışanların Eğitimi Yönetmeliği',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 8,
    code: 'employeeRepresentative',
    title: '👥 Çalışan Temsilcisi Seçim Tutanağı',
    validity: () => '5 Yılda Bir',
    legalBasis: '6331 Sayılı Kanun Madde 20',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 9,
    code: 'annualEvaluation',
    title: '📊 Yıllık Değerlendirme Raporu',
    validity: () => 'Her Yıl Sonu (1 Yıl)',
    legalBasis: 'İSG Hizmetleri Yönetmeliği EK-2',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 10,
    code: 'kkdDelivery',
    title: '🧤 KKD Teslim & Taahhüt Tutanağı',
    validity: () => 'İşe Girişte / Yenilendikçe',
    legalBasis: 'KKD Kullanım Yönetmeliği',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 11,
    code: 'firstAid',
    title: '🩹 İlkyardımcı Belgesi & Dolap Kontrolü',
    validity: () => '3 Yılda Bir',
    legalBasis: 'İlkyardım Yönetmeliği',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 12,
    code: 'machinePeriodicCheck',
    title: '⚙️ Makine & Ekipman Periyodik Kontrolleri',
    validity: () => 'Yılda 1 (Standart 1 Yıl)',
    legalBasis: 'İş Ekipmanlarının Kullanımında Sağlık ve Güvenlik',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 13,
    code: 'electricalInstallationCheck',
    title: '⚡ Elektrik & Topraklama Ölçüm Raporu',
    validity: () => 'Yılda 1 (1 Yıl)',
    legalBasis: 'Elektrik Tesislerinde Topraklamalar Yönetmeliği',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 14,
    code: 'detectionSuggestionBook',
    title: '📖 Onaylı Defter / Tespit Öneri Defteri',
    validity: () => 'Her Ziyarette Düzenli',
    legalBasis: 'İSG Hizmetleri Yönetmeliği Madde 7',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Takip Bekliyor'
  },
  {
    id: 15,
    code: 'emergencyDrill',
    title: '🔥 Acil Durum Tatbikat Raporu',
    validity: () => 'Yılda En Az 1 Defa',
    legalBasis: 'İşyerlerinde Acil Durumlar Yönetmeliği',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 16,
    code: 'fieldAuditReport',
    title: '📝 Saha Denetim & İSG Ziyaret Raporu',
    validity: () => 'Aylık Ziyaret Periyodunda',
    legalBasis: 'İSG Uzmanı Görev ve Yetkileri Yönetmeliği',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Takip Bekliyor'
  },
  {
    id: 17,
    code: 'test',
    title: '🔬 Ortam Ölçümleri & Test Raporları',
    validity: () => 'Risk Analizine Göre',
    legalBasis: 'İş Hijyeni Ölçüm, Test ve Analizleri Yön.',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  },
  {
    id: 18,
    code: 'jobTraining',
    title: '🏛️ İSG Kurul Tutanağı (50+ Çalışan)',
    validity: (danger: string) => danger === 'veryDangerous' ? 'Ayda 1' : danger === 'dangerous' ? '2 Ayda 1' : '3 Ayda 1',
    legalBasis: 'İş Sağlığı ve Güvenliği Kurulları Hakkında Yön.',
    defaultStatus: 'warning',
    defaultStatusLabel: '⚠️ Hazırlanacak'
  }
];

// Yalnızca mevzuata göre işyerinin durumuna bağlı olarak muaf tutulabilen / iptal edilebilen 5 belge çeşidi:
// 1. Çalışan Temsilcisi (employeeRepresentative)
// 2. Makine Periyodik Kontrolleri (machinePeriodicCheck)
// 3. KKD Teslim Tutanağı (kkdDelivery)
// 4. Saha Denetim Raporu (fieldAuditReport)
// 5. İSG Kurul Tutanağı (jobTraining)
export const ALLOWED_CANCELLABLE_DOCS = [
  'employeeRepresentative',
  'machinePeriodicCheck',
  'kkdDelivery',
  'fieldAuditReport',
  'jobTraining'
];

export const MONTHS_CONFIG = [
  { num: 1, name: 'Ocak' },
  { num: 2, name: 'Şubat' },
  { num: 3, name: 'Mart' },
  { num: 4, name: 'Nisan' },
  { num: 5, name: 'Mayıs' },
  { num: 6, name: 'Haziran' },
  { num: 7, name: 'Temmuz' },
  { num: 8, name: 'Ağustos' },
  { num: 9, name: 'Eylül' },
  { num: 10, name: 'Ekim' },
  { num: 11, name: 'Kasım' },
  { num: 12, name: 'Aralık' }
];

// Tehlike sınıfına göre ziyaret işaretleme kuralı:
// Az Tehlikeli: Mevcut ay ve gelecek 2 ay daha (toplam 3 ay)
// Tehlikeli: Mevcut ay ve sonraki 1 ay (toplam 2 ay)
// Çok Tehlikeli: Sadece mevcut ay (toplam 1 ay)
export const checkMonthVisitAllowed = (
  monthNum: number,
  dangerClass: string | undefined,
  currentYear: number,
  targetYear: number = new Date().getFullYear()
): { allowed: boolean; reason?: string } => {
  const now = new Date();
  const activeCurrentMonth = now.getMonth() + 1; // 1 - 12
  const activeCurrentYear = now.getFullYear();

  // Geçmiş yıllar veya aylar her zaman incelenebilir / işaretlenebilir
  if (targetYear < activeCurrentYear) return { allowed: true };
  if (targetYear === activeCurrentYear && monthNum < activeCurrentMonth) return { allowed: true };

  // Çok Tehlikeli: Yalnızca mevcut ay
  if (dangerClass === 'veryDangerous') {
    if (targetYear === activeCurrentYear && monthNum === activeCurrentMonth) {
      return { allowed: true };
    }
    return {
      allowed: false,
      reason: 'Çok Tehlikeli işyerlerinde mevzuat gereği sadece mevcut ay için ziyaret işaretlenebilir.'
    };
  }

  // Tehlikeli: Mevcut ay ve sonraki 1 ay
  if (dangerClass === 'dangerous') {
    const maxMonth = activeCurrentMonth + 1;
    if (targetYear === activeCurrentYear && monthNum <= maxMonth) {
      return { allowed: true };
    }
    return {
      allowed: false,
      reason: 'Tehlikeli işyerlerinde en fazla mevcut ay ve sonraki 1 ay için ziyaret işaretlenebilir.'
    };
  }

  // Az Tehlikeli: Mevcut ay ve gelecek 2 ay daha (Toplam 3 ay)
  const maxMonth = activeCurrentMonth + 2;
  if (targetYear === activeCurrentYear && monthNum <= maxMonth) {
    return { allowed: true };
  }
  return {
    allowed: false,
    reason: 'Az Tehlikeli işyerlerinde en fazla mevcut ay ve sonraki 2 ay için ziyaret işaretlenebilir.'
  };
};

// Belge türü ve tehlike sınıfına göre yasal geçerlilik süresi (Ay cinsinden)
export const getValidityMonths = (code: string, danger: string): number | null => {
  switch (code) {
    case 'riskAnalysis':
    case 'emergencyPlan':
      return danger === 'veryDangerous' ? 24 : danger === 'dangerous' ? 48 : 72;
    case 'machinePeriodicCheck':
    case 'electricalInstallationCheck':
    case 'emergencyDrill':
    case 'test':
      return 12;
    case 'jobTraining':
      // İSG Kurul Tutanağı (50+ Çalışan): Tehlike sınıfına göre 1 - 2 - 3 Ayda Bir
      return danger === 'veryDangerous' ? 1 : danger === 'dangerous' ? 2 : 3;
    case 'ek2':
      return danger === 'veryDangerous' ? 12 : danger === 'dangerous' ? 36 : 60;
    case 'trainings':
      return danger === 'veryDangerous' ? 12 : danger === 'dangerous' ? 24 : 36;
    case 'employeeRepresentative':
      return 60;
    case 'firstAid':
      return 36;
    case 'fieldAuditReport':
      return 1;
    case 'trainingPlan':
    case 'workPlan':
    case 'annualEvaluation':
      return 12; // Özel olarak 1 Ocak kuralıyla hesaplanır
    default:
      return null; // Sürekli / Güncel tutulan evraklar
  }
};

// Evrak için kalan süre durum rozeti
export const calculateRemainingTimeBadge = (docCode: string, dangerClass: string = 'low', files: StoredDocumentFile[]) => {
  if (!files || files.length === 0) {
    return <span className="remaining-badge none">-</span>;
  }

  // Sürekli / kalıcı olan belgeler (Talimatlar, KKD teslim vb.)
  if (['instructions', 'kkdDelivery', 'detectionSuggestionBook'].includes(docCode)) {
    return <span className="remaining-badge perpetual">♾️ Sürekli</span>;
  }

  const latestFile = files[files.length - 1];
  let expiryDate: Date | null = null;

  if (latestFile.expiryDate) {
    expiryDate = new Date(latestFile.expiryDate);
  } else {
    // Belge tarihini al (yoksa rawDate veya bugün)
    const baseDate = latestFile.documentDate ? new Date(latestFile.documentDate) : (latestFile.rawDate ? new Date(latestFile.rawDate) : new Date());
    if (!isNaN(baseDate.getTime())) {
      // 1. Yıllık Eğitim Planı ve Yıllık Çalışma Planı: Takvim yılı kuralı (Sonraki yılın 1 Ocak tarihi)
      if (['trainingPlan', 'workPlan', 'annualEvaluation'].includes(docCode)) {
        expiryDate = new Date(baseDate.getFullYear() + 1, 0, 1, 0, 0, 0, 0);
      } else {
        const validityMonths = getValidityMonths(docCode, dangerClass);
        if (validityMonths !== null) {
          expiryDate = new Date(baseDate);
          expiryDate.setMonth(expiryDate.getMonth() + validityMonths);
        }
      }
    }
  }

  if (!expiryDate || isNaN(expiryDate.getTime())) {
    return <span className="remaining-badge perpetual">♾️ Sürekli</span>;
  }

  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return (
      <span className="remaining-badge expired" title={`Bitiş: ${formatDate(expiryDate)}`}>
        ⚠️ Süresi Doldu ({Math.abs(diffDays)} gün)
      </span>
    );
  }

  if (diffDays <= 30) {
    return (
      <span className="remaining-badge urgent" title={`Bitiş: ${formatDate(expiryDate)}`}>
        ⏰ {diffDays === 0 ? 'Bugün Doluyor' : `${diffDays} Gün Kaldı`}
      </span>
    );
  }

  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    const remDays = diffDays % 30;
    return (
      <span className="remaining-badge valid" title={`Bitiş: ${formatDate(expiryDate)}`}>
        ✅ {months} Ay {remDays > 0 ? `${remDays} Gün ` : ''}Kaldı
      </span>
    );
  }

  const years = Math.floor(diffDays / 365);
  const remMonths = Math.round((diffDays % 365) / 30);
  return (
    <span className="remaining-badge valid" title={`Bitiş: ${formatDate(expiryDate)}`}>
      ✅ {years > 0 ? `${years} yıl ` : ''}{remMonths > 0 ? `${remMonths} ay ` : ''}Kaldı
    </span>
  );
};

const WorkplaceDetailModal: React.FC<WorkplaceDetailModalProps> = ({
  workplace,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onUpdate,
  onWorkplaceChange
}) => {
  const [currentWorkplace, setCurrentWorkplace] = useState<Workplace | null>(workplace);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    setCurrentWorkplace(workplace);
  }, [workplace]);

  const activeWorkplace = currentWorkplace || workplace;

  const handleWorkplaceSaved = useCallback((updated: Workplace) => {
    setCurrentWorkplace(updated);
    onWorkplaceChange?.(updated.id, updated);
    onUpdate?.();
  }, [onWorkplaceChange, onUpdate]);

  const [activeTab, setActiveTab] = useState<'bilgi' | 'ziyaret' | 'yapilan' | 'evrak'>('evrak');
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  
  // Her işyerine özel evrak takibi (localStorage anahtarı: isg_docs_wp_{workplaceId})
  const [docFilesMap, setDocFilesMap] = useState<Record<string, StoredDocumentFile[]>>({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [preSelectedDocCode, setPreSelectedDocCode] = useState<string | undefined>(undefined);
  const [viewingDocCode, setViewingDocCode] = useState<string | null>(null);

  // Her işyerine özel yapılan ziyaret ayları takibi (12 Ay, localStorage: isg_monthly_visits_wp_{workplaceId})
  const [monthlyVisits, setMonthlyVisits] = useState<Record<number, boolean>>({});

  // Oluşturulan Blob URL'lerini bellek sızıntısına (memory leak) karşı takip eden ref
  const activeBlobUrlsRef = useRef<Set<string>>(new Set());

  // Güvenli Blob URL oluşturucu
  const createTrackedBlobUrl = useCallback((file: File): string => {
    const url = URL.createObjectURL(file);
    activeBlobUrlsRef.current.add(url);
    return url;
  }, []);

  // Güvenli Blob URL temizleyici
  const revokeTrackedBlobUrl = useCallback((url?: string) => {
    if (url && activeBlobUrlsRef.current.has(url)) {
      try {
        URL.revokeObjectURL(url);
        activeBlobUrlsRef.current.delete(url);
      } catch (e) {
        console.warn('Blob URL serbest bırakılırken hata:', e);
      }
    }
  }, []);

  // Bileşen unmount olduğunda tüm açık Blob URL'lerini belleğe geri ver
  useEffect(() => {
    const activeUrls = activeBlobUrlsRef.current;
    return () => {
      activeUrls.forEach(url => {
        try { URL.revokeObjectURL(url); } catch {}
      });
      activeUrls.clear();
    };
  }, []);

  // İşyerine ait resmi belgeleri backend veritabanından çek ve senkronize et
  const loadWorkplaceDocuments = useCallback(async () => {
    if (!workplace?.id) return;
    try {
      const res = await workplaceDocumentApi.getWorkplaceDocuments(workplace.id);
      const token = localStorage.getItem('uzman_token') || localStorage.getItem('field_uzman_token');
      const tokenParam = token ? `?token=${token}` : '';
      const apiHost = (typeof window !== 'undefined' && window.location?.hostname) ? window.location.hostname : 'localhost';
      const newMap: Record<string, StoredDocumentFile[]> = {};

      if (res && res.data) {
        res.data.forEach((doc: any) => {
          const type = doc.documentType;
          if (!newMap[type]) newMap[type] = [];
          newMap[type].push({
            id: String(doc.id),
            name: doc.originalFileName || doc.fileName,
            size: doc.fileSize,
            uploadedAt: doc.uploadedDate ? formatDate(doc.uploadedDate) : '',
            rawDate: doc.expiryDate || doc.documentDate || doc.uploadedDate || doc.createdAt,
            expiryDate: doc.expiryDate,
            documentDate: doc.documentDate,
            type: doc.mimeType,
            url: `http://${apiHost}:5001/api/v1/archive/${doc.id}/preview${tokenParam}`
          });
        });
      }

      setDocFilesMap(newMap);
    } catch (err) {
      console.error('İşyeri belgeleri yüklenirken hata:', err);
    }
  }, [workplace?.id]);

  // İşyerine ait kayıtlı verileri ve aylık yapılan ziyaretleri yükle
  useEffect(() => {
    if (workplace?.id && isOpen) {
      loadWorkplaceDocuments();

      const savedVisits = localStorage.getItem(`isg_monthly_visits_wp_${workplace.id}`);
      if (savedVisits) {
        try {
          setMonthlyVisits(JSON.parse(savedVisits));
        } catch {
          setMonthlyVisits({});
        }
      } else {
        setMonthlyVisits({});
      }
    }
  }, [workplace?.id, isOpen, loadWorkplaceDocuments]);

  // İşyerine ait ziyaretleri backend'den çek ve 12 aylık ızgarayı senkronize et
  const loadVisits = useCallback(async () => {
    if (!workplace?.id) return;
    try {
      setLoadingVisits(true);
      const res = await visitApi.getByWorkplace(workplace.id);
      if (res.success && res.data) {
        setVisits(res.data);
        const currentYear = new Date().getFullYear();
        const monthMap: Record<number, boolean> = {};

        // 1. Veritabanındaki ziyaretlerden işaretli ayları çıkar
        res.data.forEach((v: any) => {
          if (v.status === 'completed' && v.visitDate && v.visitDate.startsWith(String(currentYear))) {
            const m = parseInt(v.visitDate.split('-')[1], 10);
            if (!isNaN(m)) monthMap[m] = true;
          }
        });

        // 2. İşyerinin monthlyVisits verisi varsa birleştir
        if (workplace.monthlyVisits && typeof workplace.monthlyVisits === 'object') {
          Object.keys(workplace.monthlyVisits).forEach((k) => {
            if (workplace.monthlyVisits[k]) monthMap[Number(k)] = true;
          });
        }

        setMonthlyVisits(monthMap);
      }
    } catch (err) {
      console.error('Ziyaretler yüklenirken hata oluştu:', err);
    } finally {
      setLoadingVisits(false);
    }
  }, [workplace?.id, workplace?.monthlyVisits]);

  // Aylık ziyaret kutusunu işaretleme / kaldırma (Veritabanı Senkronize & Mobil Uyumlu)
  const handleToggleMonthVisit = useCallback(async (monthNum: number) => {
    if (!workplace) return;
    const currentYear = new Date().getFullYear();
    const isCurrentlyDone = !!monthlyVisits[monthNum];

    // Eğer işaretlenmek isteniyorsa tehlike sınıfı kısıtlamasını kontrol et
    if (!isCurrentlyDone) {
      const check = checkMonthVisitAllowed(monthNum, workplace.dangerClass, currentYear);
      if (!check.allowed) {
        Swal.fire({
          icon: 'warning',
          title: 'İşaretleme Kısıtlaması',
          text: check.reason || 'Mevzuat kuralları gereği bu ay için ziyaret işaretlenemez.',
          confirmButtonColor: '#0284c7'
        });
        return;
      }
    }

    const updated = {
      ...monthlyVisits,
      [monthNum]: !isCurrentlyDone
    };
    setMonthlyVisits(updated);

    try {
      localStorage.setItem(`isg_monthly_visits_wp_${workplace.id}`, JSON.stringify(updated));
    } catch (e) {}

    // Anlık İyimser (Optimistic) Güncelleme: Dashboard'a beklemeden 0ms'de yansıt
    onWorkplaceChange?.(workplace.id, { monthlyVisits: updated });
    onUpdate?.();

    // Backend veritabanı ile çift yönlü senkronizasyon
    try {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      const currentDay = now.getDate();
      const monthStr = String(monthNum).padStart(2, '0');

      // Tıklanan tarih: İçinde bulunduğumuz ay işaretleniyorsa TAM OLARAK bugünün tarihi!
      // Geçmiş veya farklı bir ay işaretleniyorsa o ayın gün sınırını aşmayacak şekilde bugünün günü
      let targetDate: string;
      if (monthNum === currentMonth) {
        const dayStr = String(currentDay).padStart(2, '0');
        targetDate = `${currentYear}-${monthStr}-${dayStr}`;
      } else {
        const daysInMonth = new Date(currentYear, monthNum, 0).getDate();
        const safeDay = Math.min(currentDay, daysInMonth);
        const dayStr = String(safeDay).padStart(2, '0');
        targetDate = `${currentYear}-${monthStr}-${dayStr}`;
      }

      const monthName = MONTHS_CONFIG.find(m => m.num === monthNum)?.name || `${monthNum}. Ay`;

      if (!isCurrentlyDone) {
        // KURAL: Eğer mevcut veya geçmiş bir ay işaretleniyorsa gün/saat kontrolü
        // (Gelecekteki aylar veya günler ziyaret gerçekleşmeden YAPILDI olarak işaretlenemez)
        const timeCheck = canMarkVisitAsCompleted(targetDate, '09:30');
        if (!timeCheck.allowed) {
          Swal.fire({
            icon: 'warning',
            title: 'Ziyaret Henüz Gerçekleşemez',
            text: timeCheck.reason || 'Ziyaret günü ve saati gelmeden "YAPILDI" olarak işaretlenemez.',
            confirmButtonColor: '#e11d48'
          });
          // Optimistic state'i geri al
          const rolledBack = { ...monthlyVisits, [monthNum]: false };
          setMonthlyVisits(rolledBack);
          onWorkplaceChange?.(workplace.id, { monthlyVisits: rolledBack });
          return;
        }

        // Bu aya ait mevcut bir ziyaret kaydı varsa 'completed' yap ve tıklanan tarihi kaydet
        const existing = visits.find(v => v.visitDate && v.visitDate.startsWith(`${currentYear}-${monthStr}`));
        if (existing) {
          await visitApi.updateStatus(existing.id, {
            status: 'completed',
            visitDate: targetDate,
            notes: existing.notes || `${monthName} ayı saha ziyareti tamamlandı.`
          });
        } else {
          // Ziyareti tamamlandı olarak backend'e kaydet (Çakışma engeline takılmaması için allowConflict: true)
          await visitApi.create({
            workplaceId: workplace.id,
            visitDate: targetDate,
            startTime: '09:30',
            endTime: '11:30',
            visitType: 'Periyodik Saha Ziyareti',
            status: 'completed',
            notes: `${monthName} ayı saha ziyareti tamamlandı.`,
            allowConflict: true
          });
        }
      } else {
        // Ziyareti iptal et - backend'deki ilgili ayın ziyaretini bulup sil
        const existing = visits.find(v => v.visitDate && v.visitDate.startsWith(`${currentYear}-${monthStr}`));
        if (existing) {
          await visitApi.delete(existing.id);
        }
      }

      // Ayrıca workplace.monthlyVisits sütununu güncelle
      await workplaceApi.update(workplace.id, { monthlyVisits: updated });

      // Ziyaretleri yeniden yükle ve ebeveyn bileşeni senkronize et
      await loadVisits();
      onUpdate?.();
    } catch (err) {
      console.error('Ziyaret veritabanına işlenirken hata:', err);
    }
  }, [workplace, monthlyVisits, visits, loadVisits, onWorkplaceChange, onUpdate]);

  useEffect(() => {
    if (isOpen && workplace?.id) {
      loadVisits();
    }
  }, [isOpen, workplace?.id, loadVisits]);

  // Yeni belge yükleme başarılı olduğunda çağrılır (backend veritabanına ve arşive kaydeder)
  const handleUploadSuccess = useCallback(async (docCode: string, files: File[], documentDate?: string) => {
    if (!workplace?.id) return;
    try {
      const finalDocDate = documentDate || new Date().toISOString().split('T')[0];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', docCode);
        formData.append('documentDate', finalDocDate);
        await workplaceDocumentApi.uploadDocument(workplace.id, formData);
      }
      await loadWorkplaceDocuments();
      onUpdate?.();
      Swal.fire({
        icon: 'success',
        title: 'Belge Kaydedildi',
        text: 'Resmi evrak sisteme ve arşive başarıyla işlendi.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Belge yükleme hatası:', err);
      Swal.fire('Hata', 'Belge yüklenirken bir sorun oluştu.', 'error');
    }
  }, [workplace?.id, loadWorkplaceDocuments, onUpdate]);

  const openUploadForDoc = useCallback((docCode?: string) => {
    setPreSelectedDocCode(docCode);
    setIsUploadModalOpen(true);
  }, []);

  const openViewForDoc = useCallback((docCode: string) => {
    setViewingDocCode(docCode);
  }, []);

  // Tek dosya silme (Backend veritabanından kalıcı olarak kaldırır)
  const handleDeleteDocFile = useCallback(async (docCode: string, fileId: string) => {
    try {
      const numId = parseInt(fileId, 10);
      if (!isNaN(numId)) {
        await archiveApi.deleteDocument(numId);
      }
      await loadWorkplaceDocuments();
      onUpdate?.();
    } catch (err) {
      console.error('Belge silme hatası:', err);
    }
  }, [loadWorkplaceDocuments, onUpdate]);

  // Belgeye ait tüm dosyaları silme
  const handleDeleteAllDocFiles = useCallback(async (docCode: string) => {
    try {
      const files = docFilesMap[docCode] || [];
      for (const f of files) {
        const numId = parseInt(f.id, 10);
        if (!isNaN(numId)) {
          await archiveApi.deleteDocument(numId);
        }
      }
      await loadWorkplaceDocuments();
      onUpdate?.();
    } catch (err) {
      console.error('Tüm belgeleri silme hatası:', err);
    }
  }, [docFilesMap, loadWorkplaceDocuments, onUpdate]);

  // Tıklama kilit kontrolü (Hızlı çift tıklamayı ve yarış durumunu engelle)
  const isUpdatingCancelRef = useRef(false);

  // Belge İptal Et / İptali Kaldır (Muafiyet Yönetimi)
  const handleToggleCancelDocument = useCallback(async (docCode: string) => {
    if (!activeWorkplace?.id || isUpdatingCancelRef.current) return;
    isUpdatingCancelRef.current = true;

    try {
      const currentCancelled = Array.isArray(activeWorkplace.cancelledDocuments)
        ? [...activeWorkplace.cancelledDocuments]
        : [];

      const isCurrentlyCancelled = currentCancelled.includes(docCode);
      const updatedCancelled = isCurrentlyCancelled
        ? currentCancelled.filter(k => k !== docCode)
        : [...currentCancelled, docCode];

      // İyimser anlık yerel güncelleme
      const updatedWp: Workplace = {
        ...activeWorkplace,
        cancelledDocuments: updatedCancelled
      };
      setCurrentWorkplace(updatedWp);
      onWorkplaceChange?.(activeWorkplace.id, { cancelledDocuments: updatedCancelled });

      // Backend veritabanına kaydet
      const res = await workplaceApi.update(activeWorkplace.id, {
        cancelledDocuments: updatedCancelled
      });

      if (res.success && res.data) {
        // Backend'den dönen güncel kayıtla kesin senkronize et
        setCurrentWorkplace(res.data);
        onWorkplaceChange?.(activeWorkplace.id, { cancelledDocuments: res.data.cancelledDocuments });
      }

      Swal.fire({
        icon: 'success',
        title: isCurrentlyCancelled ? 'Belge Yeniden Aktif Edildi' : 'Belge Bu İşyeri İçin İptal Edildi',
        text: isCurrentlyCancelled
          ? 'Belge zorunlu İSG evrak listesine ve ilerleme puanına tekrar dahil edildi.'
          : 'Belge iptal edildi, soluklaştırıldı ve ilerleme yüzdesi hesabından çıkarıldı.',
        timer: 1600,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Belge iptal durumu güncellenirken hata:', err);
      // Geri al
      setCurrentWorkplace(activeWorkplace);
      onWorkplaceChange?.(activeWorkplace.id, { cancelledDocuments: activeWorkplace.cancelledDocuments });
      Swal.fire('Hata', 'Belge durumu güncellenemedi.', 'error');
    } finally {
      isUpdatingCancelRef.current = false;
    }
  }, [activeWorkplace, onWorkplaceChange]);

  // Tehlike sınıfı ve yasal süre hesaplamalarını useMemo ile önbellekle
  const dangerInfo = useMemo(() => {
    if (!activeWorkplace) {
      return {
        riskClass: 'risk-low',
        riskLabel: 'Az Tehlikeli',
        timeText: '0 dk'
      };
    }
    const danger = activeWorkplace.dangerClass;
    let riskClass = 'risk-low';
    let riskLabel = 'Az Tehlikeli';
    let perEmployee = 10;

    if (danger === 'veryDangerous') {
      riskClass = 'risk-verydangerous';
      riskLabel = 'Çok Tehlikeli';
      perEmployee = 40;
    } else if (danger === 'dangerous') {
      riskClass = 'risk-dangerous';
      riskLabel = 'Tehlikeli';
      perEmployee = 20;
    }

    const count = activeWorkplace.employeeCount || 1;
    const totalMinutes = perEmployee * count;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    let timeText = '';
    if (hours === 0) timeText = `${mins} dk / ay (${perEmployee} dk/çalışan)`;
    else if (mins === 0) timeText = `${hours} saat / ay (${perEmployee} dk/çalışan)`;
    else timeText = `${hours} sa ${mins} dk / ay (${perEmployee} dk/çalışan)`;

    return { riskClass, riskLabel, timeText };
  }, [activeWorkplace]);

  // Cari ay ve ziyaret analizi (Memoized - Aylık onaylar ile senkron)
  const visitMetrics = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const currentMonthNum = new Date().getMonth() + 1;
    const currentMonthStr = new Date().toISOString().slice(0, 7); // YYYY-MM
    const thisMonth = visits.filter(v => v.visitDate && v.visitDate.startsWith(currentMonthStr));
    const plannedThisMonth = thisMonth.filter(v => v.status === 'planned');
    const nextPlannedVisit = visits.find(v => v.status === 'planned');
    
    // Planlanan (veya devam eden) ziyaretler listesi
    const plannedVisits = visits.filter(v => v.status === 'planned');
    // Backend'deki tamamlanmış ziyaret kayıtları
    const completedVisits = visits.filter(v => v.status === 'completed');

    // 12 Aylık kutulardan işaretlenen aylar
    const isCurrentMonthMarkedInGrid = !!monthlyVisits[currentMonthNum];

    // Yalnızca gelmiş olan aylar (mevcut ay ve geçmiş aylar <= currentMonthNum) gerçekleşmiş sayılır
    // Gelecek aylar işaretlense dahi o ay gelene kadar gerçekleşen sayısına ve ilerlemeye dahil edilmez
    const realizedMarkedMonthsCount = Object.keys(monthlyVisits)
      .map(Number)
      .filter(num => !!monthlyVisits[num] && num <= currentMonthNum).length;

    // Gerçekleşen toplam ziyaret sayısı (Mevcut/geçmiş kutulardan işaretlenenler veya backend kayıtları)
    const totalCompletedCount = Math.max(completedVisits.length, realizedMarkedMonthsCount);

    // Cari ayda tamamlanma durumu (Kutuda işaretli veya backend'de completed)
    const isThisMonthCompleted = isCurrentMonthMarkedInGrid || thisMonth.some(v => v.status === 'completed');

    // Son gerçekleşen ziyaret bilgisi (yalnızca gerçekleşmiş/gelmiş olan aylardan)
    let lastCompletedVisitDate = visits.find(v => v.status === 'completed')?.visitDate;
    if (!lastCompletedVisitDate && realizedMarkedMonthsCount > 0) {
      // Mevcut veya geçmiş aylardan en son işaretli olanı bul
      const pastOrCurrentMarkedNums = Object.keys(monthlyVisits)
        .map(Number)
        .filter(num => !!monthlyVisits[num] && num <= currentMonthNum)
        .sort((a, b) => b - a);
      if (pastOrCurrentMarkedNums.length > 0) {
        const latestMonth = pastOrCurrentMarkedNums[0];
        const monthName = MONTHS_CONFIG.find(m => m.num === latestMonth)?.name || `${latestMonth}. Ay`;
        lastCompletedVisitDate = `${monthName} ${currentYear}`;
      }
    }

    return {
      completedThisMonth: isThisMonthCompleted ? [{ id: -1, visitDate: `${currentYear}-${String(currentMonthNum).padStart(2, '0')}-01`, status: 'completed' as const }] : [],
      plannedThisMonth,
      nextPlannedVisit,
      lastCompletedVisitDate,
      plannedVisits,
      completedVisits,
      totalCompletedCount
    };
  }, [visits, monthlyVisits]);

  // Ziyaret Tamamlama İşlemi
  const handleCompleteVisit = useCallback(async (visitId: number) => {
    try {
      const res = await visitApi.updateStatus(visitId, { status: 'completed' });
      if (res.success && res.data) {
        setVisits(prev => prev.map(v => v.id === visitId ? res.data! : v));
        await loadVisits();
        onUpdate?.();
        Swal.fire({
          icon: 'success',
          title: 'Ziyaret Tamamlandı',
          text: 'Saha ziyareti başarıyla tamamlandı olarak işaretlendi.',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (err) {
      console.error('Ziyaret güncelleme hatası:', err);
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Ziyaret durumu güncellenemedi.'
      });
    }
  }, [loadVisits, onUpdate]);

  // Ziyaret Silme İşlemi
  const handleDeleteVisit = useCallback(async (visitId: number) => {
    if (!window.confirm('Bu ziyaret kaydını silmek istediğinize emin misiniz?')) return;
    try {
      const res = await visitApi.delete(visitId);
      if (res.success) {
        setVisits(prev => prev.filter(v => v.id !== visitId));
        await loadVisits();
        onUpdate?.();
      }
    } catch (err) {
      console.error('Ziyaret silme hatası:', err);
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Ziyaret silinemedi.'
      });
    }
  }, [loadVisits, onUpdate]);

  // Zorunlu evrak durumu hesabı (Yalnızca izin verilen 5 belgeden iptal edilenler muaf sayılır)
  const cancelledDocKeys = useMemo(() => {
    const rawList = Array.isArray(activeWorkplace?.cancelledDocuments) ? activeWorkplace.cancelledDocuments : [];
    // Eski veya test sırasında yanlışlıkla eklenmiş olanlar (örn. ek2, riskAnalysis) asla muaf sayılmaz
    return rawList.filter(k => ALLOWED_CANCELLABLE_DOCS.includes(k));
  }, [activeWorkplace?.cancelledDocuments]);

  // İptal edilmemiş aktif zorunlu evraklar
  const activeMandatoryDocs = useMemo(() => {
    return OFFICIAL_ISG_DOCUMENTS.filter(doc => !cancelledDocKeys.includes(doc.code));
  }, [cancelledDocKeys]);

  const totalMandatoryDocs = activeMandatoryDocs.length;
  const completedDocsCount = useMemo(() => {
    return activeMandatoryDocs.filter(doc => (docFilesMap[doc.code] || []).length > 0).length;
  }, [activeMandatoryDocs, docFilesMap]);

  // Toplam İlerleme Hesabı:
  // 1) Yapılan Ziyaret: Yalnızca içinde bulunulan CARİ AY için ziyaret işaretlenmişse -> +%50
  // (Ay değiştiğinde o ayın ziyareti henüz yapılmamışsa puan otomatik olarak 0 olur)
  // 2) İSG Evrakları: Aktif (iptal edilmemiş) zorunlu belgelerden her biri -> (completedDocsCount / totalMandatoryDocs) * 50
  const totalProgress = useMemo(() => {
    const now = new Date();
    const activeCurrentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const isCurrentMonthMarkedInGrid = !!(monthlyVisits[activeCurrentMonth] || (monthlyVisits as any)[String(activeCurrentMonth)]);
    const hasCurrentMonthCompletedVisit = visits.some(v => {
      if (v.status !== 'completed' || !v.visitDate) return false;
      const d = new Date(v.visitDate);
      return !isNaN(d.getTime()) && d.getFullYear() === currentYear && (d.getMonth() + 1) === activeCurrentMonth;
    });

    const isThisMonthCompleted = isCurrentMonthMarkedInGrid || hasCurrentMonthCompletedVisit;
    const visitScore = isThisMonthCompleted ? 50 : 0;
    const docScore = totalMandatoryDocs > 0 ? Math.round((completedDocsCount / totalMandatoryDocs) * 50) : 50;
    return Math.min(100, visitScore + docScore);
  }, [monthlyVisits, visits, completedDocsCount, totalMandatoryDocs]);

  const progressTheme = useMemo(() => getProgressTheme(totalProgress), [totalProgress]);

  // Dropdown listesi referansını sabitle (İptal / Muaf edilen belgeler evrak ekleme seçiminde de listelenmez)
  const documentDropdownList = useMemo(() => {
    return activeMandatoryDocs.map(d => ({ id: d.id, code: d.code, title: d.title }));
  }, [activeMandatoryDocs]);

  if (!isOpen || !workplace) return null;

  return (
    <>
      <div className="pre-acc-overlay" onClick={onClose}>
        <div className="pre-acc-suite-panel" onClick={(e) => e.stopPropagation()}>
          {/* Modern Clean Header (Tek ve Net Başlık) */}
          <div className="modal-clean-header">
            <div className="modal-header-left">
              <div className="modal-title-row">
                <h2 className="modal-firm-title">{activeWorkplace.title}</h2>
                <span className={`active-danger-badge ${dangerInfo.riskClass}`}>
                  ⚠️ {dangerInfo.riskLabel}
                </span>
                <span className="active-employee-badge">
                  👥 {activeWorkplace.employeeCount} Çalışan
                </span>
              </div>
              <div className="modal-meta-row">
                {activeWorkplace.sgkNo && (
                  <span className="modal-meta-item">
                    <span className="modal-meta-label">SGK / DETSİS:</span> {activeWorkplace.sgkNo}
                  </span>
                )}
                {activeWorkplace.city && (
                  <span className="modal-meta-item">
                    📍 {activeWorkplace.city}{activeWorkplace.district ? ` / ${activeWorkplace.district}` : ''}
                  </span>
                )}
                {(activeWorkplace.contactPerson || activeWorkplace.contactPhone) && (
                  <span className="modal-meta-item">
                    👤 {activeWorkplace.contactPerson || 'Yetkili'} {activeWorkplace.contactPhone ? `(${activeWorkplace.contactPhone})` : ''}
                  </span>
                )}
              </div>
            </div>

            <div className="modal-header-right">
              {/* Sade İlerleme Göstergesi */}
              <div className="modal-header-progress" title="Ziyaret (%50) + İSG Evrakları (%50)">
                <span className="modal-progress-label">İlerleme:</span>
                <div className="modal-progress-track">
                  <div
                    className="modal-progress-bar"
                    style={{
                      width: `${totalProgress}%`,
                      background: progressTheme.barGradient
                    }}
                  />
                </div>
                <span className="modal-progress-val">
                  %{totalProgress}
                </span>
              </div>

              <button
                type="button"
                className="btn-modal-edit"
                onClick={() => setIsEditModalOpen(true)}
                title="İşyeri Bilgilerini Düzenle"
              >
                ✏️ Düzenle
              </button>

              <button className="detail-close-btn" onClick={onClose} title="Pencereyi Kapat">
                ✕
              </button>
            </div>
          </div>

          {/* Top KPI Summary Bar (Sade 3 Metrik) */}
          <div className="kpi-summary-bar">
            <div className="kpi-card">
              <div className="kpi-label">Yasal İSG Hizmet Süresi</div>
              <div className="kpi-value text-normal-slate">{dangerInfo.timeText}</div>
              <div className="kpi-sub text-normal-sub">{dangerInfo.riskLabel} • {activeWorkplace.employeeCount} Çalışan</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-label">Aylık Saha Ziyareti</div>
              <div className="kpi-value">
                {visitMetrics.completedThisMonth.length > 0 ? (
                  <span className="text-green font-bold">✅ Bu Ay Yapıldı</span>
                ) : visitMetrics.plannedThisMonth.length > 0 ? (
                  <span className="warning-text font-bold">📅 Ziyaret Planlandı</span>
                ) : (
                  <span className="blink-red-alert">🔴 Henüz Yapılmadı</span>
                )}
              </div>
              <div className="kpi-sub font-semibold">
                {visitMetrics.nextPlannedVisit
                  ? `Plan Tarihi: ${formatDate(visitMetrics.nextPlannedVisit.visitDate)}`
                  : visitMetrics.lastCompletedVisitDate
                  ? `Son Ziyaret: ${formatDate(visitMetrics.lastCompletedVisitDate)}`
                  : 'Bu ay için ziyaret bekleniyor'}
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-label">Zorunlu İSG Evrakları</div>
              <div className={`kpi-value ${completedDocsCount === totalMandatoryDocs ? 'text-green' : completedDocsCount > 0 ? 'text-purple' : 'warning-text'}`}>
                {completedDocsCount} / {totalMandatoryDocs} Tamamlandı
              </div>
              <div className="kpi-sub font-semibold">
                {completedDocsCount === totalMandatoryDocs
                  ? '✅ Tüm Evraklar Eksiksiz'
                  : completedDocsCount > 0
                  ? `📋 ${totalMandatoryDocs - completedDocsCount} Belge Hazırlanacak`
                  : '⚠️ Henüz Evrak Yüklenmedi'}
              </div>
            </div>
          </div>

          {/* Unified Single-Layer Operations Card */}
          <div className="operations-unified-card">
            <div className="operations-tab-header">
              <div className="sub-nav-bar">
                <button
                  type="button"
                  className={`sub-tab-btn evrak-tab ${activeTab === 'evrak' ? 'active' : ''}`}
                  onClick={() => setActiveTab('evrak')}
                >
                  📑 İSG Evrakları ({completedDocsCount}/{totalMandatoryDocs})
                </button>
                <button
                  type="button"
                  className={`sub-tab-btn ziyaret-tab ${activeTab === 'ziyaret' ? 'active' : ''}`}
                  onClick={() => setActiveTab('ziyaret')}
                >
                  📅 Ziyaret Planla ({visitMetrics.plannedVisits.length})
                </button>
                <button
                  type="button"
                  className={`sub-tab-btn yapilan-tab ${activeTab === 'yapilan' ? 'active' : ''}`}
                  onClick={() => setActiveTab('yapilan')}
                >
                  ✅ Yapılan Ziyaretler ({visitMetrics.totalCompletedCount})
                </button>
                <button
                  type="button"
                  className={`sub-tab-btn bilgi-tab ${activeTab === 'bilgi' ? 'active' : ''}`}
                  onClick={() => setActiveTab('bilgi')}
                >
                  🏢 Firma Bilgileri &amp; Notlar
                </button>
              </div>
            </div>

            <div className="operations-card-body">
              {/* SUB TAB: FİRMA BİLGİLERİ & NOTLAR */}
              {activeTab === 'bilgi' && (
                <div className="saas-card-panel">
                  <div className="panel-header-row">
                    <div>
                      <h3>Kurumsal &amp; İletişim Detayları</h3>
                      <p className="panel-subtitle">İşyerinin resmi kayıtları, adres ve yetkili iletişim bilgileri.</p>
                    </div>
                    <button
                      type="button"
                      className="btn-saas-secondary small"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      ✏️ Bilgileri Düzenle
                    </button>
                  </div>

                  <div className="firm-info-grid">
                    <div className="firm-info-box">
                      <div className="firm-box-header">
                        <span className="firm-box-icon">👤</span>
                        <span className="firm-box-title">Yetkili &amp; İletişim</span>
                      </div>
                      <div className="firm-box-content">
                        <div className="firm-info-row">
                          <span className="firm-info-label">Yetkili Kişi:</span>
                          <span className="firm-info-val">{activeWorkplace.contactPerson || 'Belirtilmemiş'}</span>
                        </div>
                        <div className="firm-info-row">
                          <span className="firm-info-label">Telefon:</span>
                          <span className="firm-info-val font-mono">{activeWorkplace.contactPhone || 'Belirtilmemiş'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="firm-info-box">
                      <div className="firm-box-header">
                        <span className="firm-box-icon">📑</span>
                        <span className="firm-box-title">Resmi Kayıtlar</span>
                      </div>
                      <div className="firm-box-content">
                        <div className="firm-info-row">
                          <span className="firm-info-label">SGK / DETSİS:</span>
                          <span className="firm-info-val font-mono">{activeWorkplace.sgkNo || 'Belirtilmemiş'}</span>
                        </div>
                        <div className="firm-info-row">
                          <span className="firm-info-label">Vergi No:</span>
                          <span className="firm-info-val font-mono">{activeWorkplace.taxNo || 'Belirtilmemiş'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="firm-info-box full-span">
                      <div className="firm-box-header">
                        <span className="firm-box-icon">📍</span>
                        <span className="firm-box-title">Konum &amp; Açık Adres</span>
                      </div>
                      <div className="firm-box-content">
                        <div className="firm-info-row">
                          <span className="firm-info-label">İl / İlçe:</span>
                          <span className="firm-info-val">{activeWorkplace.city || '-'}{activeWorkplace.district ? ` / ${activeWorkplace.district}` : ''}</span>
                        </div>
                        <div className="firm-info-row">
                          <span className="firm-info-label">Açık Adres:</span>
                          <span className="firm-info-val">{activeWorkplace.address || 'Kayıtlı açık adres bulunmuyor.'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="firm-info-box full-span">
                      <div className="firm-box-header">
                        <span className="firm-box-icon">📝</span>
                        <span className="firm-box-title">Özel Notlar</span>
                      </div>
                      <div className="firm-box-content">
                        <p className="firm-notes-text">
                          {activeWorkplace.notes ? activeWorkplace.notes : 'Bu işyeri için henüz özel bir not eklenmemiş.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SUB TAB 2: ZİYARET PLANLA (SARI / KEHRİBAR TEMA) */}
              {activeTab === 'ziyaret' && (
                <div className="saas-card-panel">
                  <div className="panel-header-row">
                    <div>
                      <h3>📅 Saha Ziyareti Planla &amp; Takip Programı</h3>
                      <p className="panel-subtitle">İşyerine ait yaklaşan ve planlanan periyodik saha denetim takvimi.</p>
                    </div>
                    <div className="statement-export-group">
                      <button
                        type="button"
                        className="btn-saas-primary amber small"
                        onClick={() => setIsPlanModalOpen(true)}
                      >
                        ➕ Yeni Saha Ziyareti Planla
                      </button>
                    </div>
                  </div>

                  <div className="payment-form-grid" style={{ marginBottom: '10px' }}>
                    <div className="field-group">
                      <label>Sıradaki Planlanan Ziyaret</label>
                      <input
                        type="text"
                        className="saas-input font-bold"
                        style={{ color: visitMetrics.nextPlannedVisit ? '#b45309' : '#64748b' }}
                        readOnly
                        value={visitMetrics.nextPlannedVisit ? `${formatDate(visitMetrics.nextPlannedVisit.visitDate)} (${visitMetrics.nextPlannedVisit.startTime || '09:00'} - ${visitMetrics.nextPlannedVisit.endTime || '11:00'})` : 'Planlanmış Ziyaret Yok'}
                      />
                    </div>

                    <div className="field-group">
                      <label>Bu Ayki Planlama Durumu</label>
                      <input
                        type="text"
                        className="saas-input font-bold"
                        style={{ color: '#b45309' }}
                        readOnly
                        value={
                          visitMetrics.plannedThisMonth.length > 0
                            ? `${visitMetrics.plannedThisMonth.length} / 1 Ziyaret Planlandı 📅`
                            : '0 / 1 Ziyaret (Planlama Bekliyor)'
                        }
                      />
                    </div>

                    <div className="field-group">
                      <label>Zorunlu Aylık İSG Süresi</label>
                      <input
                        type="text"
                        className="saas-input font-bold"
                        readOnly
                        value={dangerInfo.timeText}
                      />
                    </div>

                    <div className="field-group">
                      <label>Toplam Bekleyen Plan</label>
                      <input
                        type="text"
                        className="saas-input font-bold"
                        style={{ color: '#b45309' }}
                        readOnly
                        value={`${visitMetrics.plannedVisits.length} Planlı Ziyaret`}
                      />
                    </div>
                  </div>

                  <div className="statement-table-container">
                    {loadingVisits ? (
                      <div style={{ textAlign: 'center', padding: '16px', color: '#64748b' }}>
                        Ziyaret planları yükleniyor...
                      </div>
                    ) : visitMetrics.plannedVisits.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                        📅 Şu anda planlanmış bir saha ziyareti bulunmuyor. Yeni bir ziyaret tarihi belirlemek için yukarıdaki <strong>"➕ Yeni Saha Ziyareti Planla"</strong> butonuna tıklayabilirsiniz.
                      </div>
                    ) : (
                      <table className="saas-table">
                        <thead>
                          <tr>
                            <th style={{ width: '100px' }}>Plan Tarihi</th>
                            <th style={{ width: '95px' }}>Saat</th>
                            <th>Ziyaret Türü</th>
                            <th>Saha Notu / Ziyaret Amacı</th>
                            <th style={{ width: '105px', textAlign: 'center' }}>Durum</th>
                            <th style={{ width: '110px', textAlign: 'center' }}>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visitMetrics.plannedVisits.map((v) => (
                            <tr key={v.id}>
                              <td style={{ fontWeight: 700 }}>{formatDate(v.visitDate)}</td>
                              <td>{v.startTime || '09:00'} - {v.endTime || '11:00'}</td>
                              <td style={{ fontWeight: 600 }}>{v.visitType}</td>
                              <td style={{ color: v.notes ? '#334155' : '#94a3b8', fontStyle: v.notes ? 'normal' : 'italic' }}>
                                {v.notes || 'Not eklenmemiş'}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className="doc-badge-pill amber">
                                  📅 Planlandı
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                  <button
                                    type="button"
                                    className="btn-saas-secondary green small"
                                    onClick={() => handleCompleteVisit(v.id)}
                                    title="Ziyareti Tamamlandı Olarak İşaretle"
                                  >
                                    ✓ Tamamla
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-saas-secondary pink small"
                                    onClick={() => handleDeleteVisit(v.id)}
                                    title="Planı Sil"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {/* SUB TAB 3: YAPILAN ZİYARETLER (AÇIK MAVİ / TURKUAZ TEMA) */}
              {activeTab === 'yapilan' && (
                <div className="saas-card-panel">
                  <div className="panel-header-row">
                    <div>
                      <h3>✅ Yapılan Saha Ziyaretleri &amp; Aylık Ziyaret Takibi</h3>
                      <p className="panel-subtitle">İşyerinde gerçekleştirilen aylık saha denetimleri. Ziyaret yapılan ayı işaretleyerek ilerleme yüzdesine +%50 ekleyebilirsiniz.</p>
                    </div>
                  </div>

                  {/* 12 AYLIK ZİYARET KUTULARI (12 AY GRID) */}
                  <div className="monthly-visits-section">
                    <div className="monthly-visits-header">
                      <div className="monthly-title">
                        <span>🗓️</span> {new Date().getFullYear()} Yılı Aylık Ziyaret Takvimi
                      </div>
                      <div className="monthly-legend">
                        <span className="legend-item"><span className="legend-dot done"></span> Yapıldı (+%50)</span>
                        <span className="legend-item"><span className="legend-dot pending"></span> Bekliyor</span>
                        <span className="legend-item"><span className="legend-dot current"></span> Cari Ay</span>
                      </div>
                    </div>

                    <div className="monthly-grid-12">
                      {MONTHS_CONFIG.map((m) => {
                        const isDone = !!monthlyVisits[m.num];
                        const isCurrent = m.num === (new Date().getMonth() + 1);
                        const allowedCheck = checkMonthVisitAllowed(
                          m.num,
                          workplace.dangerClass,
                          new Date().getFullYear()
                        );
                        const canToggle = isDone || allowedCheck.allowed;

                        return (
                          <div
                            key={m.num}
                            className={`month-visit-box ${isDone ? 'is-done' : ''} ${isCurrent ? 'current-month' : ''}`}
                            title={!canToggle ? (allowedCheck.reason || 'Mevzuat kısıtlaması nedeniyle işaretlenemez') : undefined}
                          >
                            <div className="month-box-header">
                              <span className="month-box-name">{m.name}</span>
                              {isCurrent && <span className="month-curr-badge">BU AY</span>}
                            </div>
                            <button
                              type="button"
                              className={`month-btn-toggle ${isDone ? 'done' : ''}`}
                              onClick={() => handleToggleMonthVisit(m.num)}
                              disabled={!canToggle}
                            >
                              {isDone ? '✓ Yapıldı' : 'İşaretle'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="payment-form-grid" style={{ marginBottom: '10px' }}>
                    <div className="field-group">
                      <label>Son Gerçekleştirilen Ziyaret</label>
                      <input
                        type="text"
                        className="saas-input font-bold"
                        style={{ color: visitMetrics.lastCompletedVisitDate ? '#0369a1' : '#64748b' }}
                        readOnly
                        value={visitMetrics.lastCompletedVisitDate ? `${formatDate(visitMetrics.lastCompletedVisitDate)} (Tamamlandı)` : 'Henüz Gerçekleşen Ziyaret Yok'}
                      />
                    </div>

                    <div className="field-group">
                      <label>Bu Ay Tamamlanan</label>
                      <input
                        type="text"
                        className="saas-input font-bold"
                        style={{ color: '#0369a1' }}
                        readOnly
                        value={
                          visitMetrics.completedThisMonth.length > 0
                            ? `${visitMetrics.completedThisMonth.length} / 1 Ziyaret Gerçekleşti ✅`
                            : 'Bu ay henüz ziyaret yapılmadı'
                        }
                      />
                    </div>

                    <div className="field-group">
                      <label>Zorunlu Aylık İSG Süresi</label>
                      <input
                        type="text"
                        className="saas-input font-bold"
                        readOnly
                        value={dangerInfo.timeText}
                      />
                    </div>

                    <div className="field-group">
                      <label>Toplam Gerçekleşen Ziyaret</label>
                      <input
                        type="text"
                        className="saas-input font-bold"
                        style={{ color: '#0369a1' }}
                        readOnly
                        value={`${visitMetrics.totalCompletedCount} Ziyaret Gerçekleşti`}
                      />
                    </div>
                  </div>

                  <div className="statement-table-container">
                    {loadingVisits ? (
                      <div style={{ textAlign: 'center', padding: '16px', color: '#64748b' }}>
                        Ziyaret geçmişi yükleniyor...
                      </div>
                    ) : visitMetrics.completedVisits.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                        ℹ️ Bu işyeri için henüz tamamlanmış saha ziyareti kaydı bulunmuyor. Planlanan ziyaretleri gerçekleştirdikten sonra <strong>"✓ Tamamla"</strong> butonuna basarak buraya aktarabilirsiniz.
                      </div>
                    ) : (
                      <table className="saas-table">
                        <thead>
                          <tr>
                            <th style={{ width: '100px' }}>Ziyaret Tarihi</th>
                            <th style={{ width: '95px' }}>Saat Aralığı</th>
                            <th>Ziyaret Türü</th>
                            <th>Saha Denetim Notu / Açıklama</th>
                            <th style={{ width: '115px', textAlign: 'center' }}>Durum</th>
                            <th style={{ width: '80px', textAlign: 'center' }}>İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visitMetrics.completedVisits.map((v) => (
                            <tr key={v.id}>
                              <td style={{ fontWeight: 700, color: '#0369a1' }}>{formatDate(v.visitDate)}</td>
                              <td>{v.startTime || '09:00'} - {v.endTime || '11:00'}</td>
                              <td style={{ fontWeight: 600 }}>{v.visitType}</td>
                              <td style={{ color: v.notes ? '#334155' : '#94a3b8', fontStyle: v.notes ? 'normal' : 'italic' }}>
                                {v.notes || 'Not eklenmemiş'}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className="doc-badge-pill success">
                                  ✅ Tamamlandı
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  type="button"
                                  className="btn-saas-secondary pink small"
                                  onClick={() => handleDeleteVisit(v.id)}
                                  title="Ziyaret Kaydını Sil"
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {/* SUB TAB: İSG EVRAKLARI (18 RESMİ MEVZUAT BELGESİ) */}
              {activeTab === 'evrak' && (
                <div className="saas-card-panel">
                  <div className="panel-header-row" style={{ justifyContent: 'flex-end', marginBottom: '8px' }}>
                    <button
                      type="button"
                      className="btn-saas-primary pink small"
                      onClick={() => openUploadForDoc()}
                    >
                      📁 Yeni Belge Yükle
                    </button>
                  </div>

                  <div className="statement-table-container isg-docs-scroll">
                    <table className="saas-table">
                      <thead>
                        <tr>
                          <th style={{ width: '32px', textAlign: 'center' }}>#</th>
                          <th>Zorunlu Belge / Evrak Adı</th>
                          <th style={{ width: '130px' }}>Geçerlilik</th>
                          <th style={{ width: '135px', textAlign: 'center' }}>Kalan Süre</th>
                          <th style={{ width: '140px' }}>Dayanak Mevzuat</th>
                          <th style={{ width: '105px', textAlign: 'center' }}>Muafiyet</th>
                          <th style={{ width: '115px', textAlign: 'center' }}>Durum</th>
                          <th style={{ width: '95px', textAlign: 'center' }}>İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {OFFICIAL_ISG_DOCUMENTS.map((doc) => {
                          const files = docFilesMap[doc.code] || [];
                          const isReady = files.length > 0;
                          const isCancelled = cancelledDocKeys.includes(doc.code);
                          const isCancellable = ALLOWED_CANCELLABLE_DOCS.includes(doc.code);

                          const statusClass = isCancelled
                            ? 'cancelled'
                            : isReady
                            ? 'success'
                            : doc.defaultStatus;

                          const statusLabel = isCancelled
                            ? '🚫 MUAF'
                            : isReady
                            ? `✅ HAZIR (${files.length})`
                            : doc.defaultStatusLabel;

                          return (
                            <tr key={doc.id} className={`isg-doc-row ${isCancelled ? 'isg-doc-row-cancelled' : ''}`}>
                              <td style={{ fontWeight: 700, textAlign: 'center', color: '#64748b' }}>{doc.id}</td>
                              <td style={{ fontWeight: 600, color: isCancelled ? '#475569' : '#0f172a' }}>
                                {doc.title} {isCancelled && <small style={{ color: '#64748b', fontWeight: 700 }}> (Muaf)</small>}
                              </td>
                              <td style={{ fontWeight: 500, color: '#475569' }}>
                                {doc.validity(workplace.dangerClass)}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {isCancelled ? (
                                  <span className="remaining-badge cancelled">Muaf</span>
                                ) : (
                                  calculateRemainingTimeBadge(doc.code, workplace.dangerClass, files)
                                )}
                              </td>
                              <td style={{ fontSize: '10.5px', color: '#64748b' }}>
                                {doc.legalBasis}
                              </td>

                              {/* Ayrı Muafiyet / İptal Sütunu */}
                              <td style={{ textAlign: 'center' }}>
                                {isCancellable ? (
                                  <button
                                    type="button"
                                    className={`btn-doc-cancel-toggle ${isCancelled ? 'is-cancelled' : ''}`}
                                    onClick={() => handleToggleCancelDocument(doc.code)}
                                    title={isCancelled ? 'Muafiyeti kaldır ve belgeyi tekrar aktif et' : 'Bu işyeri için bu belgeyi iptal et / muaf tut'}
                                  >
                                    {isCancelled ? '↩️ Aktif Et' : '🚫 İptal Et'}
                                  </button>
                                ) : (
                                  <span className="cancel-not-allowed-dash" title="Bu belge mevzuat gereği tüm işyerlerinde zorunludur, muaf tutulamaz.">-</span>
                                )}
                              </td>

                              <td style={{ textAlign: 'center' }}>
                                <span className={`doc-badge-pill ${statusClass}`}>
                                  {statusLabel}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {!isCancelled ? (
                                  isReady ? (
                                    <button
                                      type="button"
                                      className="btn-saas-secondary green small"
                                      onClick={() => openViewForDoc(doc.code)}
                                      title="Evrakları Görüntüle ve Yönet"
                                    >
                                      👁️ Görüntüle
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn-saas-secondary pink small"
                                      onClick={() => openUploadForDoc(doc.code)}
                                      title="Bu belgeye dosya yükle"
                                    >
                                      + Belge Ekle
                                    </button>
                                  )
                                ) : (
                                  <span className="cancel-not-allowed-dash">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Modal Bottom Actions Bar */}
          <div className="panel-actions-bar">
            <button
              type="button"
              className="btn-saas-danger-solid"
              onClick={() => {
                if (window.confirm(`"${activeWorkplace.title}" işyerini silmek istediğinize emin misiniz?`)) {
                  onDelete(activeWorkplace.id);
                  onClose();
                }
              }}
            >
              🗑️ İşyerini Sil
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn-saas-secondary" onClick={onClose}>
                Kapat
              </button>
              <button
                type="button"
                className="btn-saas-primary green"
                onClick={() => setIsEditModalOpen(true)}
              >
                ✏️ İşyerini Düzenle
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* İşyeri Bilgilerini Düzenleme Modalı */}
      <EditWorkplaceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        workplace={activeWorkplace}
        onSaved={handleWorkplaceSaved}
      />

      {/* Saha Ziyareti Planlama Modalı */}
      <PlanVisitModal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
        workplace={activeWorkplace}
        allowChangeWorkplace={false}
        onVisitCreated={(newVisit) => {
          setVisits(prev => [newVisit, ...prev]);
          loadVisits();
          onUpdate?.();
        }}
      />

      {/* Yeni Belge / Çoklu Evrak Yükleme Modalı */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        workplace={activeWorkplace}
        documentList={documentDropdownList}
        preSelectedDocCode={preSelectedDocCode}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Belge Görüntüleme, İnceleme ve Silme Modalı */}
      <ViewDocumentModal
        isOpen={!!viewingDocCode}
        onClose={() => setViewingDocCode(null)}
        workplaceTitle={activeWorkplace.title}
        docTitle={OFFICIAL_ISG_DOCUMENTS.find(d => d.code === viewingDocCode)?.title || 'İSG Belgesi'}
        docCode={viewingDocCode || ''}
        files={viewingDocCode ? docFilesMap[viewingDocCode] || [] : []}
        onDeleteFile={handleDeleteDocFile}
        onDeleteAllFiles={handleDeleteAllDocFiles}
        onAddNewFiles={(code) => {
          setViewingDocCode(null);
          openUploadForDoc(code);
        }}
      />
    </>
  );
};

export default WorkplaceDetailModal;
